import React, { useEffect, useState } from "react";
import { Accordion, Alert, Card, Col, Form, Row } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";

import { useAppApi } from "@/App";
import { GameStreamMode, VK_LIVE_ID_VKPLAYSTORE } from "@/const";
import { DevForm, FormFieldGroup, FormFieldHelpText } from "@components/Forms/Fields";
import { fieldChange, useSubmit } from "@components/Forms/Handlers";
import { DrawProgressBar } from "@components/Game/Misc";
import { Awesome, DevModal } from "@components/Helpers";
import { CommonCard } from "@components/Pages";
import { url_reverse } from "@components/Utils";

const ConfirmModal = ({ show, setShow, apiData }) => {
    const { t } = useTranslation();
    const onFormSubmit = useSubmit();
    const isStreamStarted = apiData?.stream_started;
    const title = isStreamStarted ? t("Отключить трансляцию") : t("Запустить трансляцию");
    const body = isStreamStarted
        ? t("Вы хотите скрыть блок с трансляциями на странице игры?")
        : t("Вы хотите опубликовать трансляцию?");

    return (
        <DevModal
            show={show}
            onHide={() => setShow(false)}
            header={title}
            body={<p>{body}</p>}
            footer={
                <>
                    <DevForm.Button
                        name="action"
                        variant="secondary"
                        style={{ width: "100px" }}
                        value="save"
                        onClick={(ev) => onFormSubmit(ev, { onFinally: () => setShow(false) })}
                    >
                        {t("Нет")}
                    </DevForm.Button>
                    <DevForm.Button
                        name="action"
                        variant="primary"
                        style={{ width: "100px" }}
                        value={apiData?.stream_started ? "stop_streaming" : "start_streaming"}
                        onClick={(ev) => onFormSubmit(ev, { onFinally: () => setShow(false) })}
                    >
                        {t("Да")}
                    </DevForm.Button>
                </>
            }
            renderWrapper={(children) => <Form>{children}</Form>}
        />
    );
};

const StreamersField = ({ apiData, setApiData }) => {
    const { t } = useTranslation();
    const [streamers, setStreamers] = useState([""]);
    const [streamersAsString, setStreamersAsString] = useState("");
    const [showExtended, setShowExtended] = useState(false);
    const streamersApiData = apiData.forms_data.main?.data?.streamers_to_show || "";

    const addStreamerHandle = () => {
        const newStreamers = [...streamers];
        newStreamers.push("");
        setStreamers(newStreamers);
    };

    const changeStreamerHandle = (index, value) => {
        const newStreamers = [...streamers];
        newStreamers[index] = value;
        setStreamers(newStreamers);
    };

    const removeStreamerHandle = (index) => {
        const newStreamers = [...streamers];
        newStreamers.splice(index, 1);
        setStreamers(newStreamers);
    };

    useEffect(() => {
        setStreamers(streamersAsString.split(",").map((streamer) => streamer.trim()));
        const newApiData = { ...apiData };
        newApiData.forms_data.main.data.streamers_to_show = streamersAsString;
        setApiData(newApiData);
    }, [streamersAsString]);

    useEffect(() => {
        const stringStreamers = streamers
            .map((streamer) => streamer.trim())
            // .filter((streamer) => streamer !== "")
            .join(",");
        setStreamersAsString(stringStreamers);
    }, [streamers]);

    useEffect(() => {
        setStreamersAsString(streamersApiData);
        setStreamers(streamersApiData.split(",").map((streamer) => streamer.trim()));
    }, []);

    return (
        <>
            <FormFieldGroup label={t("Стримеры")}>
                <Row>
                    <Col>
                        <DevForm.TextInput
                            disabled={apiData?.stream_started}
                            value={streamers[0]}
                            onChange={(ev) => changeStreamerHandle(0, ev.target.value)}
                        />
                        <FormFieldHelpText
                            help_text={t(
                                "Укажите slug страницы стримера в сервисе VK Play Live. Для добавления нескольких никнеймов используйте кнопку «+»."
                            )}
                        />
                    </Col>
                    {!apiData?.stream_started && (
                        <Col md={1}>
                            <DevForm.Button variant="light" size="sm" name="add_stream" onClick={addStreamerHandle}>
                                <Awesome type="plus" />
                            </DevForm.Button>
                        </Col>
                    )}
                </Row>
                {streamers.slice(1).map((streamer, index) => (
                    <Row key={`streamer_${index + 1}`} className="mt-2">
                        <Col>
                            <DevForm.TextInput
                                disabled={apiData?.stream_started}
                                value={streamers[index + 1]}
                                onChange={(ev) => changeStreamerHandle(index + 1, ev.target.value)}
                            />
                        </Col>
                        {!apiData?.stream_started && (
                            <Col md={1}>
                                <DevForm.Button
                                    variant="danger"
                                    size="sm"
                                    name="remove_stream"
                                    onClick={() => removeStreamerHandle(index + 1)}
                                >
                                    <Awesome type="trash" />
                                </DevForm.Button>
                            </Col>
                        )}
                    </Row>
                ))}
            </FormFieldGroup>
            <FormFieldGroup>
                <DevForm.Button variant="secondary" name="extended" onClick={() => setShowExtended(!showExtended)}>
                    {t("Расширенная настройка")}
                </DevForm.Button>
                {showExtended && (
                    <>
                        <DevForm.TextArea
                            disabled={apiData?.stream_started}
                            className="mt-2"
                            name="comment"
                            value={streamersAsString}
                            onChange={(ev) => setStreamersAsString(ev.target.value)}
                        />
                        <FormFieldHelpText
                            help_text={t("Поле предназначено для загрузки списка стримеров slug'ами через запятую.")}
                        />
                    </>
                )}
            </FormFieldGroup>
        </>
    );
};

export default function Streams({ gameId, header }) {
    const { t } = useTranslation();
    const onFormSubmit = useSubmit();
    const apiUrl = url_reverse("api:game:stream", { game_id: gameId });
    const { apiData, setApiData } = useAppApi(apiUrl);
    const [isAuto, setIsAuto] = useState(false);
    const [isManual, setIsManual] = useState(false);
    const [isCombo, setIsCombo] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const mainForm = apiData?.forms_data?.main.data;
    const fullChoices = [
        { value: "", label: "---------" },
        { value: "auto", label: t("Автоматический") },
        { value: "manual", label: t("Ручной") },
        { value: "combo", label: t("Комбинированный") },
    ];
    const reducedChoices = [
        { value: "", label: "---------" },
        { value: "manual", label: t("Ручной") },
        { value: "combo", label: t("Комбинированный") },
    ];
    const progressBarLabels = [t("Настройка трансляции"), t("Трансляция запущена")];
    const helpText = {
        auto: t(
            "Автоматический режим — трансляции любого стримера VK Play Live по игре будут отображаться на странице игры."
        ),
        manual: t("Ручной режим — отображаются только трансляции стримеров, добавленных в настройках вручную."),
        combo: t("Комбинированный режим — отображаются трансляции по условиям, заданным в настройках."),
    };

    useEffect(() => {
        if (mainForm) {
            setIsAuto(mainForm.stream_mode === GameStreamMode.AUTO);
            setIsManual(mainForm.stream_mode === GameStreamMode.MANUAL);
            setIsCombo(mainForm.stream_mode === GameStreamMode.COMBO);

            if (mainForm.vk_live_id.trim().length > 0 && mainForm.stream_mode === "") {
                if (mainForm.vk_live_id === VK_LIVE_ID_VKPLAYSTORE) {
                    mainForm.stream_mode = GameStreamMode.MANUAL;
                } else {
                    mainForm.stream_mode = GameStreamMode.AUTO;
                }
            }

            if (helpText[mainForm.stream_mode]) {
                const newApiData = { ...apiData };
                newApiData.forms_data.main.fields.stream_mode.help_text = helpText[mainForm.stream_mode];
                setApiData(newApiData);
            }
        }
    }, [JSON.stringify(mainForm)]);

    useEffect(() => {
        if (mainForm) {
            if (mainForm.vk_live_id === VK_LIVE_ID_VKPLAYSTORE) {
                const newApiData = { ...apiData };
                newApiData.forms_data.main.fields.stream_mode.widget.choices = reducedChoices;
                setApiData(newApiData);
            }
        }
    }, [apiData?.forms_data]);

    if (!apiData) return;

    const fieldProps = {
        apiData,
        setApiData,
    };
    const changeVkLiveId = (props) => {
        fieldChange(props);
        if (mainForm.vk_live_id !== "") {
            const newApiData = { ...apiData };
            if (mainForm.vk_live_id === VK_LIVE_ID_VKPLAYSTORE) {
                if (isAuto || !apiData?.stream_started) {
                    mainForm.stream_mode = GameStreamMode.MANUAL;
                }
                newApiData.forms_data.main.fields.stream_mode.widget.choices = reducedChoices;
            } else {
                newApiData.forms_data.main.fields.stream_mode.widget.choices = fullChoices;
                if (!apiData.stream_started) {
                    mainForm.stream_mode = GameStreamMode.AUTO;
                }
            }
            setApiData(newApiData);
        }
    };
    const headerTools = (
        <a href="https://documentation.vkplay.ru/f2p_vkp/f2p_streams_vkp" target="_blank">
            {t("Документация")}
        </a>
    );
    return (
        <CommonCard header={header} headerTools={headerTools}>
            <div className="mb-3">
                <DrawProgressBar labels={progressBarLabels} active={apiData?.stream_started ? 3 : 1} now={50} />
            </div>

            <Alert variant="info">
                <Trans>
                    В шапку страницы игры можно добавить трансляции. В блоке будут проигрываться стримы на{" "}
                    <a href="https://live.vkplay.ru/" target="_blank">
                        VK Play Live
                    </a>
                    . Для настройки трансляций, пожалуйста, заполните поля ниже.
                </Trans>
            </Alert>

            <div className="dmg-callout dmg-callout-info mb-3">
                {t("Разрешены трансляции только по данной игре. При нарушении правил блок трансляций будет отключён.")}
            </div>

            <Form onSubmit={onFormSubmit}>
                <DevForm.Field
                    {...fieldProps}
                    prefix="main"
                    name="vk_live_id"
                    onChange={(props) => changeVkLiveId(props)}
                />
                <DevForm.Field {...fieldProps} prefix="main" name="stream_mode" />
                {(isManual || isCombo) && <StreamersField apiData={apiData} setApiData={setApiData} />}
                {(isAuto || isCombo) && (
                    <>
                        <DevForm.Field {...fieldProps} prefix="main" name="stream_count" />
                        <DevForm.Field {...fieldProps} prefix="main" name="stream_min_viewers" />
                    </>
                )}

                {isAuto && (
                    <DevForm.Field
                        {...fieldProps}
                        prefix="main"
                        name="streamers_to_hide"
                        prepend="https://vkplay.live/"
                    />
                )}
                <Accordion className="mb-3">
                    <Accordion.Toggle
                        as={Card.Header}
                        eventKey="main"
                        variant="link"
                        onClick={() => setCollapsed(!collapsed)}
                    >
                        <strong className="mr-2">{t("Настройка показа блока трансляции")}</strong>
                        <Awesome type={collapsed ? "angle-up" : "angle-down"} />
                    </Accordion.Toggle>
                    <Accordion.Collapse eventKey="main">
                        <Card.Body>
                            <DevForm.Field {...fieldProps} prefix="main" name="show_stream_from" />
                            <DevForm.Field {...fieldProps} prefix="main" name="show_stream_till" />
                        </Card.Body>
                    </Accordion.Collapse>
                </Accordion>

                <DevForm.Field {...fieldProps} prefix="main" name="stream_gc_expanded" />

                <div className="mt-5">
                    <DevForm.Button
                        variant="primary"
                        type="submit"
                        name="action"
                        value="save"
                        disabled={apiData?.stream_started}
                    >
                        {t("Сохранить")}
                    </DevForm.Button>

                    <div className="float-right">
                        <DevForm.Button variant="warning" name="show_modal" onClick={() => setShowModal(!showModal)}>
                            {apiData?.stream_started ? t("Отключить трансляцию") : t("Запустить трансляцию")}
                        </DevForm.Button>
                    </div>
                </div>
                <ConfirmModal show={showModal} setShow={setShowModal} apiData={apiData} />
            </Form>
        </CommonCard>
    );
}
